// [PURPOSE]: Register class feature effects (SRD + custom Content Workshop)
//   into effectRegistry on class/level change.
// [WHY]: srdCharacterData stores feature names only; srdClassFeatures.js
//   provides the SRD mechanical wiring, and CW-authored custom classes store
//   their own `featureEffects` map. This hook is the single bridge that walks
//   both producers and registers them under prefix `class:<ClassName>:<FeatureName>`
//   (and `:<OptionName>` for choice features). Mirrors useSubclassFeatureEffects.
// [INTEGRATION]: Mount once near the top of App.jsx alongside useRaceEffects /
//   useFeatEffects. Watches playerStats.classes and re-registers on changes.
// [Phase 4b bucket 2 — APR 2026]: Extended with choices + choiceKey support so
//   character-level choice features (Fighter Fighting Style stored at
//   playerStats.fightingStyle, etc.) register the matching option's effects without
//   hardcoded class-feature special cases.
// [Phase 4b CW-parity (A) — APR 2026]: Added bridge for Content-Workshop-authored
//   custom classes so player-built effects round-trip identically to SRD ones.

import { useEffect } from 'react';
import { effectRegistry } from '../services/effectRegistry';
import { effectCalculator } from '../services/effectCalculator';
import { getActiveClassFeatures } from '../data/srdClassFeatures';
import { sourcesManager } from '../data/sourcesManager';
import { getClassData } from '../utils/contentResolver';
import { multiclassProfsToGrantEffects } from '../utils/multiclassProficiencyGrants';

/**
 * Walks a dotted path inside playerStats (e.g., 'fightingStyle', 'metamagicOptions').
 * Returns null when playerStats or choiceKey is missing; undefined when the path
 * doesn't resolve to a value.
 *
 * NOTE: choiceKey for class features walks the FULL playerStats (top-level),
 * unlike useSubclassFeatureEffects.resolveChoice which walks classEntry.subclassData.
 * This matches the canonical storage path for character-level choices (Fighting Style
 * at playerStats.fightingStyle, etc.).
 */
export function resolveClassChoice(playerStats, choiceKey) {
  if (!playerStats || !choiceKey) return null;
  return choiceKey.split('.').reduce((acc, k) => (acc != null ? acc[k] : undefined), playerStats);
}

/**
 * [Phase B / #045 — MAY 2026]: Expand "synthetic" effect types that
 *   bridge a player-data field (e.g., the chosen Pact of the Tome
 *   cantrips at playerStats.pactTomeCantrips) into N concrete
 *   registrable effects. The atWillSpellsFromField synthetic effect
 *   schema:
 *
 *     { type: 'atWillSpellsFromField',
 *       operation: 'grant',
 *       sourcePath: 'pactTomeCantrips' }
 *
 *   Each name in playerStats[sourcePath] (an array of strings)
 *   produces one concrete:
 *
 *     { type: 'atWillSpell', operation: 'grant', value: <name> }
 *
 *   Effects without a recognized synthetic type pass through unchanged.
 *   Synthetic effects whose source field is missing/empty are dropped
 *   silently — so a Warlock who picked Pact of the Tome but hasn't
 *   filled in cantrips yet registers nothing here.
 *
 * @param {Effect[]} effects — input effect list (may include synthetics)
 * @param {object} playerStats — character data, used to resolve sourcePath
 * @returns {Effect[]} concrete, registrable effect list (synthetics expanded)
 */
export function expandFromFieldEffects(effects, playerStats) {
  if (!Array.isArray(effects)) return [];
  const out = [];
  for (const effect of effects) {
    if (!effect || effect.type !== 'atWillSpellsFromField') {
      out.push(effect);
      continue;
    }
    const sourcePath = effect.sourcePath;
    const names = sourcePath
      ? sourcePath.split('.').reduce((acc, k) => (acc != null ? acc[k] : undefined), playerStats)
      : null;
    if (!Array.isArray(names) || names.length === 0) continue;
    for (const name of names) {
      if (typeof name !== 'string' || !name) continue;
      out.push({ type: 'atWillSpell', operation: 'grant', value: name });
    }
  }
  return out;
}

/**
 * [Phase 4b CW-parity (A)]: Extracts active feature entries from a
 * Content-Workshop-authored custom class. Mirrors getActiveCustomSubclassFeatures.
 *
 * Custom classes store their mechanical wiring in a top-level `featureEffects`
 * object that mirrors the per-feature shape used by SRD_CLASS_FEATURES:
 *
 *   {
 *     featureEffects: {
 *       [featureName]: {
 *         minLevel: number,
 *         effects?: Effect[],
 *         choiceKey?: string,
 *         choices?: { [option]: { effects, notes? } },
 *         notes?: string,
 *       }
 *     }
 *   }
 *
 * Returns [name, featureData] tuples filtered by minLevel <= classLevel, in
 * insertion order — same shape as getActiveClassFeatures().
 */
export function getActiveCustomClassFeatures(customClass, classLevel) {
  if (!customClass || typeof classLevel !== 'number') return [];
  const featureEffects = customClass.featureEffects;
  if (!featureEffects || typeof featureEffects !== 'object') return [];
  return Object.entries(featureEffects).filter(([, feature]) => {
    if (!feature || typeof feature.minLevel !== 'number') return false;
    return feature.minLevel <= classLevel;
  });
}

/**
 * Looks up the matching custom class entry by display name, scanning
 * sourcesManager.getAllClasses(). Returns null if none found or if the
 * lookup fails. Match is by display name only (unlike subclass which also
 * filters by parent class) — class names are intended to be globally unique.
 */
function findCustomClass(className) {
  let all;
  try {
    all = sourcesManager.getAllClasses();
  } catch (err) {
    console.warn('[useClassFeatureEffects] getAllClasses failed:', err);
    return null;
  }
  if (!all || typeof all !== 'object') return null;
  for (const entry of Object.values(all)) {
    if (entry && entry.name === className) return entry;
  }
  return null;
}

/**
 * Registers a single class feature's effects (always-on + choice-dependent)
 * under the canonical prefix. Shared between SRD and custom paths so attribution
 * and cleanup are identical.
 */
function registerFeature({ className, featureName, featureData, playerStats }) {
  if (Array.isArray(featureData.effects) && featureData.effects.length) {
    const concrete = expandFromFieldEffects(featureData.effects, playerStats);
    if (concrete.length) {
      try {
        effectRegistry.register(
          `class:${className}:${featureName}`,
          concrete,
          playerStats
        );
      } catch (err) {
        console.warn(`[useClassFeatureEffects] failed to register ${className}:${featureName}`, err);
      }
    }
  }

  if (featureData.choices && featureData.choiceKey) {
    const selected = resolveClassChoice(playerStats, featureData.choiceKey);
    const optionData = selected ? featureData.choices[selected] : null;
    if (optionData && Array.isArray(optionData.effects) && optionData.effects.length) {
      const concrete = expandFromFieldEffects(optionData.effects, playerStats);
      if (concrete.length) {
        try {
          effectRegistry.register(
            `class:${className}:${featureName}:${selected}`,
            concrete,
            playerStats
          );
        } catch (err) {
          console.warn(
            `[useClassFeatureEffects] choice failed: ${className}:${featureName}:${selected}`,
            err
          );
        }
      }
    }
  }
}

/**
 * Top-level orchestrator — exported so the bridge can be exercised in unit tests
 * without a React renderer. Walks playerStats.classes, registers SRD class
 * features, then merges in Content-Workshop-authored custom classes for the
 * same className.
 *
 * Pure side-effect: writes to effectRegistry, then clears effectCalculator cache.
 */
export function registerClassFeaturesForCharacter(playerStats) {
  effectRegistry.removeByPrefix('class:');

  const classes = playerStats?.classes || [];
  for (let index = 0; index < classes.length; index++) {
    const classEntry = classes[index];
    const className = classEntry?.className;
    const classLevel = classEntry?.level || 0;
    if (!className) continue;

    // 1. SRD class features
    const srdFeatures = getActiveClassFeatures(className, classLevel);
    for (const [featureName, featureData] of srdFeatures) {
      registerFeature({ className, featureName, featureData, playerStats });
    }

    // 2. Custom class features (CW-authored, stored in sourcesManager).
    //    Match by class name. Note: nothing prevents a custom class from
    //    sharing a name with an SRD class — both will register, both will
    //    apply. By-design behavior; gives players room to extend SRD classes.
    const customClass = findCustomClass(className);
    if (customClass) {
      const customFeatures = getActiveCustomClassFeatures(customClass, classLevel);
      for (const [featureName, featureData] of customFeatures) {
        registerFeature({ className, featureName, featureData, playerStats });
      }
    }

    // [Phase B / #045 — MAY 2026]: Pact of the Tome at-will cantrips
    //   are now declarative. The Warlock 'Pact Boon' choice for
    //   'Pact of the Tome' carries an `atWillSpellsFromField` effect
    //   pointing at playerStats.pactTomeCantrips. registerFeature →
    //   expandFromFieldEffects expands that into one atWillSpell per
    //   cantrip name, registered under the choice's canonical sourceId
    //   (`class:Warlock:Pact Boon:Pact of the Tome`). The hardcoded
    //   `className === 'Warlock' && pactBoon === 'Pact of the Tome'`
    //   branch that used to live here is gone.

    // 3. [MULTICLASS-PROF-APPLY 2026-06-14]: multiclass proficiency grants.
    //   Only NON-origin classes (index > 0) contribute the limited multiclass
    //   proficiency subset (PHB p.163). The origin class (index 0) already gets
    //   its FULL proficiencies at character creation (CharacterCreationWizard
    //   :738-747 → direct arrays). Generic + data-driven: any class exposing
    //   `multiclassProficiencies` (custom CW classes, or SRD classes like the
    //   Tasha's Artificer) grants it; PHB classes that lack the data no-op
    //   (MULTICLASS-PROF-SRD-DATA breadcrumb). The armor/weapon/tool readers
    //   already union these via getGrantedProficiencyLevel (subclass-bridge
    //   precedent, 92d85fb5) — registering here is the producer half.
    //   NOTE: index>0 assumes classes[0] is the origin class (true for 5e;
    //   MULTICLASS-PROF-ORIGIN-ASSUMPTION breadcrumb for RSE).
    if (index > 0) {
      // customClass (findCustomClass, matched by .name) takes precedence on a
      //   name collision (custom-shadows-SRD convention); getClassData covers SRD
      //   + Tasha's (name-keyed). getAllClasses() is keyed by storage key, not
      //   name, so getClassData alone can miss a custom class — hence the union.
      const classDef = customClass || getClassData(className);
      const mcEffects = multiclassProfsToGrantEffects(classDef?.multiclassProficiencies);
      if (mcEffects.length) {
        try {
          effectRegistry.register(
            `class:${className}:Multiclass Proficiencies`,
            mcEffects,
            playerStats
          );
        } catch (err) {
          console.warn(`[useClassFeatureEffects] multiclass prof register failed: ${className}`, err);
        }
      }
    }
  }

  effectCalculator.clearCache();
}

export function useClassFeatureEffects(playerStats) {
  useEffect(() => {
    registerClassFeaturesForCharacter(playerStats);

    return () => {
      effectRegistry.removeByPrefix('class:');
      effectCalculator.clearCache();
    };
    // [Phase 4b bucket 2 — APR 2026]: Add character-level choice paths to deps so
    // changes to playerStats.fightingStyle (and similar future paths) re-register
    // effects. playerStats?.classes covers per-class state; explicit paths cover
    // top-level choice storage.
    // [Phase 5 — APR 2026]: Add pactBoon + pactTomeCantrips so Pact of the Tome
    // atWillSpell effects register immediately after the wizard commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerStats?.classes, playerStats?.fightingStyle, playerStats?.pactBoon, playerStats?.pactTomeCantrips]);
}

export default useClassFeatureEffects;
